import { cloneDeep } from 'lodash';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import Asset from 'src/app/model/asset';
import { ImmoTaskAsset } from 'src/app/model/immo-task/immo-task-asset';
import { IMMO_TASK_ASSET_TYPE_CHOICES } from 'src/app/model/immo-task/immo-task-asset-type';
import LoggedAction from 'src/app/model/logged-action';
import Address from 'src/app/model/project/address';
import { TourItem } from 'src/app/model/tour/tour-item';
import { User } from 'src/app/model/user';
import { ImmoTaskType } from '../../../../overview/immo-task/model/immo-task-type';

export interface ImmoTaskForEditBase {
  id: number;

  createdAtDateTime: Date;

  subject: string;

  description: string;

  needAtelier: boolean;

  atelierInfo: string;

  requestedASAP: boolean;

  requestedDate: Date;

  address: Address;

  executionTime: string;

  executed: boolean;

  executionFeedback: string;

  executedRegistration: LoggedAction;

  type: ImmoTaskType;
}

export interface ImmoTaskForEdit extends ImmoTaskForEditBase {
  tourItem: TourItem;

  requestingUser: User;

  assets: ImmoTaskAsset[];
}

export interface ImmoTaskEditable extends ImmoTaskForEditBase {
  placerId: number;

  tourDate: Date;

  locked: boolean;

  requestingUserId: number;

  designAssets: ImmoTaskAsset[];

  offlineDesignAssets: Asset[];

  locationAssets: ImmoTaskAsset[];

  offlineLocationAssets: Asset[];

  executionAssets: ImmoTaskAsset[];

  offlineExecutionAssets: Asset[];
}

const EMPTY_EDITABLE: ImmoTaskEditable = {
  id: null,
  createdAtDateTime: null,
  subject: null,
  description: null,
  needAtelier: true,
  atelierInfo: null,
  requestedASAP: true,
  requestedDate: null,
  address: new Address(),
  executionTime: null,
  executed: null,
  executionFeedback: null,
  executedRegistration: null,
  placerId: null,
  tourDate: null,
  locked: false,
  requestingUserId: null,
  designAssets: [],
  offlineDesignAssets: [],
  locationAssets: [],
  offlineLocationAssets: [],
  executionAssets: [],
  offlineExecutionAssets: [],
  type: null,
};

export function getNewEditable(): ImmoTaskEditable {
  return cloneDeep(EMPTY_EDITABLE);
}

export function getImmoTaskEditableFromForEdit(
  immoTaskForEdit: ImmoTaskForEdit
): ImmoTaskEditable {
  if (immoTaskForEdit == null) {
    return null;
  }

  const placerId =
    immoTaskForEdit.tourItem == null ||
    immoTaskForEdit.tourItem.tour == null ||
    immoTaskForEdit.tourItem.tour.placer == null
      ? null
      : immoTaskForEdit.tourItem.tour.placer.id;
  const tourDate =
    immoTaskForEdit.tourItem == null || immoTaskForEdit.tourItem.tour == null
      ? null
      : immoTaskForEdit.tourItem.tour.date;
  const locked =
    immoTaskForEdit.tourItem == null ? null : immoTaskForEdit.tourItem.locked;
  const requestingUserId =
    immoTaskForEdit.requestingUser == null
      ? null
      : immoTaskForEdit.requestingUser.id;
  const designAssets: ImmoTaskAsset[] = [];
  const locationAssets: ImmoTaskAsset[] = [];
  const executionAssets: ImmoTaskAsset[] = [];
  const type = immoTaskForEdit.type;

  if (immoTaskForEdit.address == null) {
    immoTaskForEdit.address = new Address();
  }

  if (CollectionHelper.isPopulated(immoTaskForEdit.assets)) {
    immoTaskForEdit.assets.forEach((a) => {
      if (a.type == null) {
        return;
      } else if (
        a.type.ordinal === IMMO_TASK_ASSET_TYPE_CHOICES.DESIGN.ordinal
      ) {
        designAssets.push(a);
      } else if (
        a.type.ordinal === IMMO_TASK_ASSET_TYPE_CHOICES.LOCATION.ordinal
      ) {
        locationAssets.push(a);
      } else if (
        a.type.ordinal === IMMO_TASK_ASSET_TYPE_CHOICES.EXECUTION.ordinal
      ) {
        executionAssets.push(a);
      }
    });
  }

  return {
    id: immoTaskForEdit.id,
    createdAtDateTime: immoTaskForEdit.createdAtDateTime,
    subject: immoTaskForEdit.subject,
    description: immoTaskForEdit.description,
    needAtelier: immoTaskForEdit.needAtelier,
    atelierInfo: immoTaskForEdit.atelierInfo,
    requestedASAP: immoTaskForEdit.requestedASAP,
    requestedDate: immoTaskForEdit.requestedDate,
    address: immoTaskForEdit.address,
    executionTime: immoTaskForEdit.executionTime,
    executed: immoTaskForEdit.executed,
    executionFeedback: immoTaskForEdit.executionFeedback,
    executedRegistration: immoTaskForEdit.executedRegistration,
    placerId: placerId,
    tourDate: tourDate,
    locked: locked,
    requestingUserId: requestingUserId,
    designAssets: designAssets,
    offlineDesignAssets: [],
    locationAssets: locationAssets,
    offlineLocationAssets: [],
    executionAssets: executionAssets,
    offlineExecutionAssets: [],
    type: type,
  };
}
